import { useEffect, useState } from "react";
import ReactSelect from "react-select";
import { useOutletContext } from "react-router-dom";
import {
  listPermisosModulos,
  createPermisosModulos,
  deletePermisosModulos
} from "../api/permisosModulos";
import { createPermisos } from "../api/permisos"
import { listModulos } from "../api/modulos";
import Loader from "../components/Loader";
import { showConfirmAlert } from "../utils/alertHelper";

const Permisos = () => {
  const [loading, setLoading] = useState(true);
  const [permisos, setlistPermisosModulos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const { setShowHelpModal, setHelpMessage } = useOutletContext();
  const [showModal, setShowModal] = useState(false);
  const [modulos, setListModulos] = useState([]);
  const [selectedPermiso, setSelectedPermiso] = useState(null);
  const [selectedModulo, setSelectedModulo] = useState(null);
  const [nuevoPermisoNombre, setNuevoPermisoNombre] = useState({});
  const [nuevaDescription, setNuevaDescription] = useState({});
  const [agregandoPermiso, setAgregandoPermiso] = useState({});

  // Estados para permisos
  const [listar, setListar] = useState(true);
  const [agregar, setAgregar] = useState(false);
  const [editar, setEditar] = useState(false);
  const [imprimir, setImprimir] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await listPermisosModulos({});
        setlistPermisosModulos(data.data || []);

        const modu = await listModulos({});
        setListModulos(modu.data || []);
      } catch (error) {
        console.error("Error al obtener los permisos", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAddModuloClick = (permiso) => {
    setSelectedPermiso(permiso);
    setSelectedModulo(null);
    setListar(true);
    setAgregar(false);
    setEditar(false);
    setImprimir(false);
    setShowModal(true);
  };


  const handleDeleteModuloClick = async (id) => {
    deletePermisosModulos(id);
    const data = await listPermisosModulos({});
    setlistPermisosModulos(data.data || []);
  };


  const handleSelectChange = (selectedOption) => {
    setSelectedModulo(selectedOption);
  };


  const handleSaveModulo = async () => {
    if (!selectedModulo) return;

    // Verificar si el módulo es de tipo 2 (Módulo Premium/Restringido)
    const moduloFull = modulos.find(m => m.id.toString() === selectedModulo.value);

    // Nueva lógica: Verificar si el módulo YA está asignado a CUALQUIER permiso de CUALQUIER sucursal
    const isAlreadyAssignedGlobally = permisos.some(sucursal =>
      sucursal.Permisos.some(p =>
        p.PermisosModulos.some(pm => pm.id_modulo.toString() === selectedModulo.value)
      )
    );

    if (moduloFull && moduloFull.tipo === 2 && !isAlreadyAssignedGlobally) {
      const confirmed = await showConfirmAlert(
        "Módulo Restringido",
        "Este módulo solo puede ser activado por el equipo de **Fac2ra**. ¿Deseas ponerte en contacto con soporte?"
      );

      if (confirmed) {
        setHelpMessage(`Hola equipo de Fac2ra, solicito la activación del módulo premium: ${moduloFull.modulo}.`);
        setShowHelpModal(true);
      }
      return; // No permitir guardar si no está activado previamente
    }

    setSubmitting(true);
    try {
      const payload = {
        id_permiso: selectedPermiso.id,
        id_modulo: selectedModulo.value, // enviar el id
        listar,
        agregar,
        editar,
        imprimir,
        status: 1,
      };
      await createPermisosModulos(payload);

      const data = await listPermisosModulos({});
      setlistPermisosModulos(data.data || []);

      setShowModal(false);
    } catch (error) {
      console.error("Error al guardar el módulo", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Opciones filtradas para el select dentro del modal, según el permiso seleccionado
  const opcionesModuloFiltradas = selectedPermiso
    ? modulos
      .filter(
        (m) =>
          !selectedPermiso.PermisosModulos.some(
            (pm) => pm.id_modulo === m.id
          )
      )
      .map((m) => ({
        value: m.id.toString(),
        label: `${m.modulo} | ${m.description}`,
      }))
    : [];


  const handleInputChange = (idSucursal, field, value) => {
    if (field === "nombre") {
      setNuevoPermisoNombre((prev) => ({ ...prev, [idSucursal]: value }));
    } else if (field === "description") {
      setNuevaDescription((prev) => ({ ...prev, [idSucursal]: value }));
    }
  };

  const handleAddPermiso = async (idSucursal) => {
    if (!nuevoPermisoNombre[idSucursal]?.trim()) return;
    setAgregandoPermiso((prev) => ({ ...prev, [idSucursal]: true }));
    try {
      const payload = {
        permiso: nuevoPermisoNombre[idSucursal],
        description: nuevaDescription[idSucursal],
        id_sucursal: idSucursal,
        status: 1,
      };

      await createPermisos(payload);

      const data = await listPermisosModulos({});
      setlistPermisosModulos(data.data || []);

      // Limpiar solo el input de esta sucursal
      setNuevoPermisoNombre((prev) => ({ ...prev, [idSucursal]: "" }));
      setNuevaDescription((prev) => ({ ...prev, [idSucursal]: "" }));
    } catch (error) {
      console.error("Error al crear permiso:", error);
    } finally {
      setAgregandoPermiso((prev) => ({ ...prev, [idSucursal]: false }));
    }
  };


  if (loading) return <Loader />;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Gestión de Permisos</h2>
          <p className="text-sm text-slate-500 mt-1">Configure los niveles de acceso por sucursal y módulo</p>
        </div>
      </div>

      {permisos.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <p className="text-slate-500 font-medium">Sin sucursales asignadas</p>
        </div>
      ) : (
        permisos.map((sucursal) => (
          <div
            key={sucursal.id}
            className="bg-white border border-slate-200 shadow-sm p-6 rounded-2xl mb-6 transition-all hover:shadow-md"
          >
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
              <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  {sucursal.nombre}
                  <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md text-xs font-medium font-mono">
                    {sucursal.codigo_sucursal}
                  </span>
                </h3>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl mb-6 flex flex-col md:flex-row items-end gap-3">
              <div className="flex-grow w-full">
                <label className="block mb-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wider">Nombre del Permiso</label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white disabled:bg-slate-100 disabled:text-slate-500"
                  placeholder="Ej. Administrador, Cajero..."
                  value={nuevoPermisoNombre[sucursal.id] || ""}
                  onChange={(e) => handleInputChange(sucursal.id, "nombre", e.target.value)}
                  disabled={agregandoPermiso[sucursal.id] || false}
                />
              </div>
              <div className="flex-grow w-full">
                <label className="block mb-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wider">Descripción</label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white disabled:bg-slate-100 disabled:text-slate-500"
                  placeholder="Breve descripción del rol..."
                  value={nuevaDescription[sucursal.id] || ""}
                  onChange={(e) => handleInputChange(sucursal.id, "description", e.target.value)}
                  disabled={agregandoPermiso[sucursal.id] || false}
                />
              </div>
              <button
                onClick={() => handleAddPermiso(sucursal.id)}
                disabled={agregandoPermiso[sucursal.id] || false}
                className={`py-2.5 px-6 rounded-lg font-medium transition-all flex items-center justify-center gap-2 whitespace-nowrap text-white w-full md:w-auto h-[46px] ${agregandoPermiso[sucursal.id]
                  ? "bg-indigo-400 cursor-not-allowed shadow-none"
                  : "bg-indigo-600 hover:bg-indigo-700 shadow-sm hover:shadow-indigo-200"
                  }`}
              >
                {agregandoPermiso[sucursal.id] ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Agregando...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Nuevo Rol
                  </>
                )}
              </button>
            </div>

            {sucursal.Permisos.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                <p className="text-slate-500 font-medium">No hay roles/permisos configurados para esta sucursal.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {sucursal.Permisos.map((permiso) => {
                  const modulosAsignadosIds = permiso.PermisosModulos.map(
                    (m) => m.Modulo.id
                  );
                  const modulosDisponibles = modulos.filter(
                    (m) => !modulosAsignadosIds.includes(m.id)
                  );

                  return (
                    <div key={permiso.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                      <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <h4 className="font-bold text-slate-800 flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            {permiso.permiso}
                          </h4>
                          <p className="text-sm text-slate-500 mt-0.5 ml-7">{permiso.description}</p>
                        </div>
                        {modulosDisponibles.length > 0 && (
                          <button
                            onClick={() => handleAddModuloClick(permiso)}
                            className="bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-50 hover:text-indigo-600 transition-colors text-xs font-medium shadow-sm flex items-center gap-1.5 shrink-0 w-fit"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Asignar Módulo
                          </button>
                        )}
                      </div>

                      <div className="p-0">
                        {permiso.PermisosModulos.length === 0 ? (
                          <div className="p-6 text-center text-slate-400 text-sm italic">
                            Este rol aún no tiene acceso a ningún módulo.
                          </div>
                        ) : (
                          <div className="overflow-x-auto custom-scrollbar">
                            <table className="w-full text-sm text-left">
                              <thead className="text-xs text-slate-500 bg-white uppercase border-b border-slate-200">
                                <tr>
                                  <th className="px-5 py-3 font-semibold">Módulo</th>
                                  <th className="px-5 py-3 font-semibold text-center w-20">Listar</th>
                                  <th className="px-5 py-3 font-semibold text-center w-20">Agregar</th>
                                  <th className="px-5 py-3 font-semibold text-center w-20">Editar</th>
                                  <th className="px-5 py-3 font-semibold text-center w-20">Imprimir</th>
                                  <th className="px-5 py-3 font-semibold text-center w-24">Acción</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {permiso.PermisosModulos.map((modulo) => (
                                  <tr key={modulo.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-5 py-3">
                                      <div className="font-semibold text-slate-700">{modulo.Modulo.modulo}</div>
                                      <div className="text-xs text-slate-500 mt-0.5">{modulo.Modulo.description}</div>
                                    </td>
                                    <td className="px-5 py-3 text-center">
                                      {modulo.listar ? (
                                        <svg className="w-5 h-5 text-emerald-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                      ) : (
                                        <svg className="w-5 h-5 text-rose-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                      )}
                                    </td>
                                    <td className="px-5 py-3 text-center">
                                      {modulo.agregar ? (
                                        <svg className="w-5 h-5 text-emerald-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                      ) : (
                                        <svg className="w-5 h-5 text-rose-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                      )}
                                    </td>
                                    <td className="px-5 py-3 text-center">
                                      {modulo.editar ? (
                                        <svg className="w-5 h-5 text-emerald-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                      ) : (
                                        <svg className="w-5 h-5 text-rose-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                      )}
                                    </td>
                                    <td className="px-5 py-3 text-center">
                                      {modulo.imprimir ? (
                                        <svg className="w-5 h-5 text-emerald-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                      ) : (
                                        <svg className="w-5 h-5 text-rose-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                      )}
                                    </td>
                                    <td className="px-5 py-3 text-center">
                                      <button
                                        onClick={() => handleDeleteModuloClick(modulo.id)}
                                        className="bg-rose-50 text-rose-600 border border-rose-100 px-2.5 py-1.5 rounded-md hover:bg-rose-100 transition-colors text-xs font-medium"
                                        title="Revocar acceso"
                                      >
                                        Remover
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))
      )}

      {/* Modal */}
      {showModal && selectedPermiso && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-[100] p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8 animate-fade-in-up">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800">Agregar Módulo</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Rol: {selectedPermiso.permiso}</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <label className="block mb-2 text-sm font-semibold text-slate-700">Seleccionar Módulo</label>
                <ReactSelect
                  options={opcionesModuloFiltradas}
                  value={selectedModulo}
                  onChange={handleSelectChange}
                  placeholder="Seleccione un módulo..."
                  isSearchable
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="mb-2">
                <label className="block mb-3 text-sm font-semibold text-slate-700">Permisos a Conceder</label>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { id: "listar", label: "Listar Registros", state: listar, setter: setListar, color: "text-indigo-600", bg: "bg-indigo-600" },
                    { id: "agregar", label: "Agregar Registros", state: agregar, setter: setAgregar, color: "text-emerald-600", bg: "bg-emerald-600" },
                    { id: "editar", label: "Editar Registros", state: editar, setter: setEditar, color: "text-amber-600", bg: "bg-amber-600" },
                    { id: "imprimir", label: "Imprimir / Exportar", state: imprimir, setter: setImprimir, color: "text-blue-600", bg: "bg-blue-600" },
                  ].map((perm) => (
                    <label key={perm.id} className="relative flex items-center p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
                      <div className="flex items-center h-5">
                        <input
                          type="checkbox"
                          className={`w-4 h-4 rounded border-slate-300 ${perm.color} focus:ring-${perm.bg.replace('bg-', '')}`}
                          checked={perm.state}
                          onChange={() => perm.setter(!perm.state)}
                          disabled={submitting}
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <span className="font-medium text-slate-700">{perm.label}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3 rounded-b-2xl">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-white focus:ring-2 focus:ring-slate-200 transition-colors"
                disabled={submitting}
              >
                Cancelar
              </button>

              <button
                type="button"
                disabled={submitting || !selectedModulo}
                onClick={handleSaveModulo}
                className={`px-6 py-2.5 rounded-lg flex items-center justify-center gap-2 font-medium shadow-sm transition-all ${submitting || !selectedModulo
                    ? "bg-indigo-400 cursor-not-allowed shadow-none"
                    : "bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-200"
                  } text-white`}
              >
                {submitting && (
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                )}
                {submitting ? "Guardando..." : "Asignar Módulo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Permisos;
